import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { GraduationCap } from 'lucide-react';
import { useStudents } from '../../../hooks/useStudents';
import { EmptyState } from '../../../components/EmptyState';

export function StudentsTab({ clientId, clientName }: { clientId: string; clientName: string }) {
  const { t } = useTranslation('clients');
  const { t: ts } = useTranslation('students');
  const { data, isLoading } = useStudents({ clientId, pageSize: 100, status: 'all' });
  const students = data?.items;

  if (isLoading) {
    return (
      <div className="divide-y divide-surface-200 rounded-md border border-surface-200 bg-surface-0 px-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="py-3">
            <div className="h-4 w-40 animate-pulse rounded bg-primary/10" />
          </div>
        ))}
      </div>
    );
  }

  if (!students?.length) {
    return (
      <EmptyState
        kind="teach"
        icon={GraduationCap}
        title={t('detail.emptyStudentsTitle')}
        body={t('detail.emptyStudentsBody', { name: clientName })}
        // The add flow lives on the students list, which is where the payer
        // picker is; this tab has no dialog of its own to open.
        action={{ label: t('detail.emptyStudentsCta'), to: '/students' }}
      />
    );
  }

  return (
    <div className="divide-y divide-surface-200 border border-surface-200 rounded-md bg-surface-0">
      {students.map((s) => (
        <Link
          key={s.id}
          to={`/students/${s.id}`}
          className="flex items-center justify-between px-4 py-3 hover:bg-surface-100"
        >
          <span className="font-medium">{s.name}</span>
          <span className="flex items-center gap-1.5 text-sm text-surface-500">
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                s.status === 'ACTIVE' ? 'bg-brand-500' : 'bg-surface-300'
              }`}
            />
            {ts(`status.${s.status}`)}
          </span>
        </Link>
      ))}
    </div>
  );
}
